(() => {
  const selector = '[data-ot-multibuy-cents]';
  const cashSelector = '[data-ot-cash-progress]';
  const locale = document.documentElement.lang || 'en';
  const formatters = new Map();

  // v1.0 (2026-10-14): amounts are written in the shop's own money format ("€26,10", like the prices on the
  // page) instead of the browser-locale format ("€26.10"). sections/ot-v10-global.liquid renders 1234.56 in
  // the visitor's currency as window.otMoneySample; without it, or for another currency, Intl is used as before.
  let shopPattern;
  const getShopPattern = currency => {
    if (shopPattern === undefined) {
      shopPattern = null;
      try {
        const sample = window.otMoneySample;
        const active = window.Shopify?.currency?.active;
        if (typeof sample === 'string' && active) {
          const text = new DOMParser().parseFromString(sample, 'text/html').documentElement.textContent.trim();
          const match = /^(\D*?)1(\D?)234(\D)56(\D*)$/.exec(text);
          if (match) shopPattern = {currency: active, prefix: match[1], group: match[2], decimal: match[3], suffix: match[4]};
        }
      } catch (_) {}
    }
    return shopPattern && shopPattern.currency === currency ? shopPattern : null;
  };

  const formatMoney = (rawAmount, currency) => {
      const precision = new Intl.NumberFormat(locale, {style: 'currency', currency}).resolvedOptions().maximumFractionDigits;
      const scale = 10 ** precision;
      const amount = Math.round((rawAmount + Number.EPSILON) * scale) / scale;
      const minimumFractionDigits = Number.isInteger(amount) ? 0 : precision;
      const pattern = precision === 2 ? getShopPattern(currency) : null;
      if (pattern) {
        const [whole, fraction] = amount.toFixed(minimumFractionDigits).split('.');
        return pattern.prefix + whole.replace(/\B(?=(\d{3})+(?!\d))/g, pattern.group) + (fraction ? pattern.decimal + fraction : '') + pattern.suffix;
      }
      const key = `${currency}:${minimumFractionDigits}`;
      if (!formatters.has(key)) {
        formatters.set(key, new Intl.NumberFormat(locale, {
          style: 'currency', currency, currencyDisplay: 'narrowSymbol',
          minimumFractionDigits, maximumFractionDigits: precision
        }));
      }
      return formatters.get(key).format(amount);
  };

  // 2026-10-08: the multi-buy amounts are set in EUR and converted with Shopify's rate, which showed
  // thresholds like "$78.88" or "£58.65". Converted amounts are now shown as whole units: spend
  // thresholds rounded up (spending the shown amount always qualifies), savings rounded down (the
  // discount at checkout is never smaller than shown). Applied cart discounts stay exact.
  const wholeUnits = (rawAmount, kind) => {
    if (kind === 'spend') return Math.ceil(rawAmount - 1e-9);
    if (kind === 'mb') return Math.max(1, Math.floor(rawAmount + 1e-9));
    return rawAmount;
  };

  const update = element => {
    const currency = element.dataset.otCurrency;
    const baseCents = Number(element.dataset.otMultibuyCents);
    const appliedValue = Number(element.dataset.otAppliedCents);
    const appliedCents = Number.isFinite(appliedValue) ? Math.max(0, appliedValue) : 0;
    const shopifyCurrency = window.Shopify?.currency;
    const rate = currency === 'EUR' ? 1 : Number(shopifyCurrency?.rate);
    if (!Number.isFinite(baseCents) || baseCents <= 0) return;
    if (appliedCents <= 0 && (shopifyCurrency?.active !== currency || !Number.isFinite(rate) || rate <= 0)) return;

    try {
      const converted = baseCents * rate / 100;
      const rawAmount = appliedCents > 0 ? appliedCents / 100 : (currency === 'EUR' ? converted : wholeUnits(converted, element.dataset.otKind));
      const text = formatMoney(rawAmount, currency);
      if (element.textContent !== text) element.textContent = text;
    } catch (_) {}
  };

  const updateCash = element => {
    const currency = element.dataset.otCurrency;
    const shopifyCurrency = window.Shopify?.currency;
    const rate = currency === 'EUR' ? 1 : Number(shopifyCurrency?.rate);
    if (!Number.isFinite(rate) || rate <= 0 || (currency !== 'EUR' && shopifyCurrency?.active !== currency)) return;

    try {
      const quantity = Math.max(0, Number(element.dataset.otEligibleQuantity) || 0);
      const spend = Math.max(0, Number(element.dataset.otEligibleCents) || 0);
      const applied = Math.max(0, Number(element.dataset.otCashAppliedCents) || 0);
      const appliedTier = Math.max(0, Number(element.dataset.otCashAppliedTier) || 0);
      const precision = new Intl.NumberFormat(locale, {style: 'currency', currency}).resolvedOptions().maximumFractionDigits;
      const scale = 10 ** precision;
      const convert = cents => Math.round(Math.round((cents * rate / 100 + Number.EPSILON) * scale) / scale * 100);
      const thresholds = element.dataset.otSpendTiers.split(',').map(Number).map(convert);
      const savings = element.dataset.otSavingTiers.split(',').map(Number).map(convert);
      if (thresholds.length !== 3 || savings.length !== 3 || [...thresholds, ...savings].some(value => !Number.isFinite(value) || value <= 0)) return;

      let tier = appliedTier;
      [2, 3, 4].forEach((count, index) => {
        if (quantity >= count || spend >= thresholds[index]) tier = Math.max(tier, count);
      });
      const current = element.querySelector('[data-ot-cash-current]');
      const next = element.querySelector('[data-ot-cash-next]');
      const bar = element.querySelector('[data-ot-cash-bar]');
      const fill = element.querySelector('[data-ot-cash-fill]');
      if (!current || !next || !bar || !fill) return;

      const setText = (target, value) => {
        if (target.textContent !== value) target.textContent = value;
      };
      current.hidden = applied <= 0;
      if (applied > 0) {
        const template = tier >= 4 ? element.dataset.otMax : element.dataset.otCurrent;
        setText(current, template.replaceAll('[[DISCOUNT]]', formatMoney(applied / 100, currency)));
      }
      next.hidden = tier >= 4;
      let progress = 100;
      if (tier < 4) {
        const target = tier === 3 ? 4 : tier === 2 ? 3 : 2;
        const index = target - 2;
        const needCount = Math.max(0, target - quantity);
        const needAmount = Math.max(0, thresholds[index] - spend);
        const template = needCount === 1 ? element.dataset.otNextOne : element.dataset.otNextOther;
        const text = template.replaceAll('[[COUNT]]', String(needCount))
          .replaceAll('[[AMOUNT]]', formatMoney(needAmount / 100, currency))
          .replaceAll('[[DISCOUNT]]', formatMoney(currency === 'EUR' ? savings[index] / 100 : wholeUnits(savings[index] / 100, 'mb'), currency));
        setText(next, text);
        progress = Math.round(Math.min(100, Math.max(quantity / target, spend / thresholds[index]) * 100));
      }
      const width = `${progress}%`;
      if (fill.style.width !== width) fill.style.width = width;
      bar.setAttribute('aria-valuenow', String(progress));
      bar.setAttribute('aria-valuetext', tier >= 4 ? current.textContent : next.textContent);
      bar.hidden = false;
    } catch (_) {}
  };

  const updateWithin = root => {
    if (root instanceof Element && root.matches(selector)) update(root);
    root.querySelectorAll?.(selector).forEach(update);
    if (root instanceof Element && root.matches(cashSelector)) updateCash(root);
    root.querySelectorAll?.(cashSelector).forEach(updateCash);
  };

  const start = () => {
    updateWithin(document);
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node instanceof Element) updateWithin(node);
      }));
    }).observe(document.body, {childList: true, subtree: true});
    window.addEventListener('pageshow', () => updateWithin(document));
    window.addEventListener('themeCartOpened', () => updateWithin(document));
    window.addEventListener('shopify:section:load', event => updateWithin(event.target));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();

